/**
 * `useAgentActivity` — OBSERVED tier fetch for the Live Agent view
 * (ADR-0161 §4).
 *
 * Reads via the existing `ReportingAPI` bag (`useOptionalApi()`), so no
 * new HTTP surface is introduced. Mirrors `useAgentRegistry`'s tagged-
 * state shape (`idle | loading | error | ready`).
 *
 * Behavior (ADR-0161 §4):
 *   - `siteId == null` OR `edgeAgentIds.length === 0` → `{ kind: 'idle' }`
 *     and NO fetch. Grok, `custom`, and `unverified` always land here.
 *   - Otherwise fires the fetch on mount + when the tuple
 *     `(siteId, edgeAgentIds.join(','), range)` changes.
 *   - Any error path → `{ kind: 'error' }` (never throws; view stays
 *     usable when the API is unreachable).
 *   - Uses `useOptionalApi()` — hook is safe in test contexts without
 *     an `<ApiProvider>`.
 *
 * Gate order matches the ADR: the site + edge-id gate short-circuits
 * BEFORE the api-null check, so a browsing operator switching from a
 * gated-out agent (e.g. Grok) never issues a stray fetch — the
 * rate-limit budget is only spent on agents that structurally can
 * have observed traffic.
 */

import { useEffect, useState } from 'react';

import type { ReportingAgentActivity, ReportingRange, SiteId } from '@sill/types';

import { useOptionalApi } from '../../../api/api-context.js';

export type UseAgentActivityState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; data: ReportingAgentActivity };

export interface UseAgentActivityArgs {
  siteId: SiteId | null;
  edgeAgentIds: readonly string[];
  range: ReportingRange;
}

export interface UseAgentActivityResult {
  readonly state: UseAgentActivityState;
}

export function useAgentActivity(
  args: UseAgentActivityArgs,
): UseAgentActivityResult {
  const api = useOptionalApi();
  const { siteId, edgeAgentIds, range } = args;

  // Stable primitive derived from the array so the effect deps compare
  // by value, not by reference — a fresh `edgeAgentIds` array on every
  // render (roster is `readonly` but the caller might spread) would
  // otherwise refire the fetch each render.
  const agentIdsKey = edgeAgentIds.join(',');

  const [state, setState] = useState<UseAgentActivityState>(() =>
    siteId === null || edgeAgentIds.length === 0
      ? { kind: 'idle' }
      : { kind: 'loading' },
  );

  useEffect(() => {
    // Gate — no site or no edge ids → idle, NO fetch.
    if (siteId === null || edgeAgentIds.length === 0) {
      setState({ kind: 'idle' });
      return;
    }
    // No API provider (test context) → idle, NO fetch. Same posture as
    // `useAgentRegistry` — an absent bag is not an error.
    if (api === null) {
      setState({ kind: 'idle' });
      return;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    api.reporting
      .getAgentActivity(siteId, { range, agent_ids: edgeAgentIds })
      .then((data) => {
        if (cancelled) return;
        setState({ kind: 'ready', data });
      })
      .catch(() => {
        // Never throws; the panel renders the quiet
        // "(couldn't load activity)" footer.
        if (cancelled) return;
        setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps — the
    // stringified id key is the effective identity of `edgeAgentIds`.
  }, [api, siteId, agentIdsKey, range]);

  return { state };
}
